import { IThemeColorSet } from '../resource/interfaces';

// Convert hex to RGB
export const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 0, g: 0, b: 0 };
};

// Convert RGB to hex
export const rgbToHex = (r: number, g: number, b: number): string => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

// Adjust color opacity
export const adjustOpacity = (hex: string, opacity: number): string => {
  const rgb = hexToRgb(hex);
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
};

// Lighten color
export const lightenColor = (hex: string, percent: number): string => {
  const rgb = hexToRgb(hex);
  const factor = 1 + percent / 100;
  return rgbToHex(
    Math.min(255, Math.round(rgb.r * factor)),
    Math.min(255, Math.round(rgb.g * factor)),
    Math.min(255, Math.round(rgb.b * factor))
  );
};

// Darken color
export const darkenColor = (hex: string, percent: number): string => {
  const rgb = hexToRgb(hex);
  const factor = 1 - percent / 100;
  return rgbToHex(
    Math.max(0, Math.round(rgb.r * factor)),
    Math.max(0, Math.round(rgb.g * factor)),
    Math.max(0, Math.round(rgb.b * factor))
  );
};

// Generate color set from main color
export const generateColorSet = (mainColor: string): IThemeColorSet => {
  const primary = mainColor;
  const secondary = lightenColor(mainColor, 20);
  const accent = lightenColor(mainColor, 40);
  const background = lightenColor(mainColor, 90);
  const surface = lightenColor(mainColor, 95);
  const text = darkenColor(mainColor, 80);
  const textSecondary = darkenColor(mainColor, 60);
  const border = lightenColor(mainColor, 70);
  const shadow = adjustOpacity(mainColor, 0.1);
  const tableText = mainColor;

  // Generate gradient colors
  const gradientColors: string[] = [];
  const mainRgb = hexToRgb(mainColor);
  
  for (let i = 0; i < 8; i++) {
    const factor = (i + 1) / 8;
    const newRgb = {
      r: Math.round(mainRgb.r + (255 - mainRgb.r) * factor * 0.8),
      g: Math.round(mainRgb.g + (255 - mainRgb.g) * factor * 0.8),
      b: Math.round(mainRgb.b + (255 - mainRgb.b) * factor * 0.8),
    };
    gradientColors.push(rgbToHex(newRgb.r, newRgb.g, newRgb.b));
  }

  return {
    primary,
    secondary,
    accent,
    background,
    surface,
    text,
    textSecondary,
    border,
    shadow,
    gradientColors,
    tableText,
  };
};

// Validate hex color
export const isValidHexColor = (color: string): boolean => {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
};

